import React, {
  useState,
  useContext,
  createContext,
  useEffect,
  useRef,
} from "react";
import { Fade } from "react-awesome-reveal";

const GroupContext = createContext();

export const SelectionGroup = ({
  SelectedComponent,
  UnselectedComponent,
  defaultItem,
  children,
  className,
  id,
  SelectedItemMask,
}) => {
  // Filter valid children to ensure indices are correct
  const validChildren = React.Children.toArray(children).filter(
    (child) => child && React.isValidElement(child)
  );

  // Initialize selectedItem with defaultItem, clamped to valid range
  const getInitialSelectedItem = () => {
    const validCount = React.Children.toArray(children).filter(
      (child) => child && React.isValidElement(child)
    ).length;
    if (defaultItem !== null && defaultItem !== undefined && defaultItem < validCount) {
      return defaultItem;
    }
    return validCount > 0 ? 0 : null;
  };

  const [selectedItem, setSelectedItem] = useState(getInitialSelectedItem);
  const itemsRef = useRef([]);
  const groupRef = useRef(null);
  const [selectedItemRect, setSelectedItemRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const resizeObserver = useRef();

  // Ensure selectedItem is within valid bounds
  const safeSelectedItem = selectedItem !== null && selectedItem < validChildren.length 
    ? selectedItem 
    : (validChildren.length > 0 ? 0 : null);

  useEffect(() => {
    if (resizeObserver.current) resizeObserver.current.disconnect();
    resizeObserver.current = new ResizeObserver(() => {
      if (safeSelectedItem !== null && itemsRef.current[safeSelectedItem]) {
        const itemRect = itemsRef.current[safeSelectedItem].getBoundingClientRect();
        const groupRect = groupRef.current.getBoundingClientRect();
        setSelectedItemRect({
          x: itemRect.x - groupRect.x,
          y: itemRect.y - groupRect.y,
          width: itemRect.width,
          height: itemRect.height,
        });
      }
    });
    resizeObserver.current.observe(groupRef.current);
  }, [itemsRef, groupRef, safeSelectedItem]);
  
  const handleItemClick = (index) => {
    setSelectedItem(index);
  };

  useEffect(() => {
    // Update selectedItem when defaultItem changes, clamped to valid range
    const validCount = validChildren.length;
    if (validCount === 0) {
      return;
    }
    
    if (defaultItem !== null && defaultItem !== undefined && defaultItem < validCount) {
      // Only update if different to avoid unnecessary re-renders
      if (selectedItem !== defaultItem) {
        setSelectedItem(defaultItem);
      }
    } else if (selectedItem === null || selectedItem >= validCount) {
      // Fallback to first item if current selection is invalid
      setSelectedItem(0);
    }
  }, [defaultItem, validChildren.length, selectedItem]);

  return (
    <div className={`${className} relative except`} ref={groupRef} id={id}>
      <GroupContext.Provider
        value={{
          SelectedComponent: SelectedComponent,
          UnselectedComponent: UnselectedComponent,
        }}
      >
        <div
          className={`absolute w-[100px] h-[40px] top-[${selectedItemRect.y}px] left-[${selectedItemRect.x}px] w-[${selectedItemRect.width}px] h-[${selectedItemRect.height}px] transition ease-in-out ${SelectedItemMask}`}
        ></div>
        <Fade cascade damping={0.2}>
          {validChildren.map((child, index) => {
            if (!child || !React.isValidElement(child)) {
              return null;
            }
            return React.cloneElement(child, {
              selected: index === safeSelectedItem,
              onClick: () => handleItemClick(index),
              setRef: (el) => (itemsRef.current[index] = el),
            });
          })}
        </Fade>
      </GroupContext.Provider>
    </div>
  );
};

export const SelectionItem = ({
  selected,
  onClick,
  SelectedItem,
  UnselectedItem,
  children,
  setRef,
  disabled,
  onItemClick, // New prop for individual item click handling
}) => {
  const { SelectedComponent, UnselectedComponent } = useContext(GroupContext);
  const [selectedA, setSelectedA] = useState(selected);

  useEffect(() => {
    // Update immediately to prevent both items appearing selected
    setSelectedA(selected);
  }, [selected]);

  const handleClick = () => {
    if (!disabled) {
      onClick(); // Call the group's selection handler
      if (onItemClick) {
        onItemClick(); // Call the individual item's click handler
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className="cursor-pointer select-none w-full relative"
      ref={(el) => setRef(el)}
    >
      {selectedA
        ? SelectedItem
          ? SelectedItem
          : SelectedComponent
        : UnselectedItem
        ? UnselectedItem
        : UnselectedComponent}
      {children}
    </div>
  );
};
